import { IF, NO, UITextView, UIView } from "uicore-ts"
import { RHColors, RHFont, RHRadius, RHSpacing } from "./Utils_slop/RHDesignTokens"


export type RHCalloutVariant = "info" | "important" | "warning"


export class RHCalloutView extends UIView {

    readonly _iconLabel: UITextView
    readonly _titleLabel: UITextView
    readonly _bodyLabel: UITextView
    static readonly _iconColumnWidth = RHSpacing.xLarge

    constructor(variant: RHCalloutVariant, title: string, body: string) {
        super()
        let backgroundColor = RHColors.primaryBlueSurfaceFaint
        let borderColor = RHColors.primaryBlue
        let iconText = "i"
        let iconColor = RHColors.primaryBlue
        if (variant === "important") {
            backgroundColor = RHColors.successGreenSurfaceFaint
            borderColor = RHColors.successGreen
            iconText = "✓"
            iconColor = RHColors.successGreenDark
        }
        else if (variant === "warning") {
            backgroundColor = RHColors.warningAmberSurfaceDraft
            borderColor = RHColors.warningAmberBorderDraft
            iconText = "!"
            iconColor = RHColors.warningAmberTextDeep
        }
        ;(this as RHCalloutView).configureWithObject({
            backgroundColor,
            style: {
                border: `1px solid ${borderColor.stringValue}`,
                borderLeft: `4px solid ${borderColor.stringValue}`,
                borderRadius: `${RHRadius.medium}px`
            }
        })
        this._iconLabel = new UITextView().configuredWithObject({
            text: iconText,
            textColor: RHColors.white,
            textAlignment: UITextView.textAlignment.center,
            backgroundColor: iconColor,
            style: {
                borderRadius: "50%",
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightBold,
                lineHeight: `${RHSpacing.large}px`
            }
        }).addedAsSubviewToView(this)
        this._iconLabel.viewHTMLElement.setAttribute("aria-hidden", "true")
        this._titleLabel = new UITextView().configuredWithObject({
            text: title,
            textColor: RHColors.textHeading,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
        this._bodyLabel = new UITextView().configuredWithObject({
            text: body,
            textColor: RHColors.textPrimary,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
    }

    get bodyText(): string {
        return this._bodyLabel.text
    }

    set bodyText(body: string) {
        this._bodyLabel.text = body
        this.setNeedsLayout()
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.medium)
        for (const label of [this._titleLabel, this._bodyLabel]) {
            if (label.text.trim()) {
                if (label.superview !== this) {
                    label.addedAsSubviewToView(this)
                }
            }
            else {
                label.removeFromSuperview()
            }
        }
        const firstTextLabel = IF(this._titleLabel.text.trim())(() => this._titleLabel)
            .ELSE(() => this._bodyLabel)
        const firstLineHeight = IF(firstTextLabel.text.trim())(() =>
            parseFloat(firstTextLabel.textElementView.computedStyle.lineHeight)
        ).ELSE(() => RHSpacing.large)
        const firstRowHeight = Math.max(RHSpacing.large, firstLineHeight)
        bounds
            .rectangleWithHeight(firstRowHeight)
            .rectangleWithWidth(RHSpacing.large)
            .rectangleWithHeight(RHSpacing.large, 0.5)
            .assignedAsFrameOfView(this._iconLabel)
            .rectangleWithWidth(RHCalloutView._iconColumnWidth)
            .rectangleForNextColumn(0, bounds.width - RHCalloutView._iconColumnWidth)
            .rectangleWithHeight(firstRowHeight, 0.5)
            .rectangleWithHeight(firstLineHeight, 0.5)
            .IF(this._titleLabel.text.trim().length > 0)
                .rectangleWithHeight(this._titleLabel)
                .assignedAsFrameOfView(this._titleLabel)
                .IF(this._bodyLabel.text.trim().length > 0)
                    .rectangleForNextRow(RHSpacing.tiny, this._bodyLabel)
                    .assignedAsFrameOfView(this._bodyLabel)
                .ENDIF()
            .ELSE_IF(this._bodyLabel.text.trim().length > 0)
                .rectangleWithHeight(this._bodyLabel)
                .assignedAsFrameOfView(this._bodyLabel)
            .ENDIF()
    }

}
